import { useRef, useState } from 'react'
import { ArrowUpDown, Diamond, Download, Pencil, Plus, SlidersHorizontal, Star, X } from 'lucide-react'
import RatingFilterRow from './RatingFilterRow'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import ConfirmDialog from '@/components/ui/confirm-dialog'
import SelectionBar from '@/components/ui/selection-bar'
import {
  DropdownMenu, DropdownMenuCheckItem, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import AchievementCard from '@/components/feature/AchievementCard'
import AchievementDetailDialog from '@/components/feature/AchievementDetailDialog'
import AchievementCreateSheet from '@/components/feature/AchievementCreateSheet'
import ExternalImportSheet from '@/components/feature/ExternalImportSheet'
import { deleteAchievement as deleteAchievementApi } from '@/api/achievements'
import { useAchievements, useBulkResetAiAnalysis, useDeleteAchievement } from '@/hooks/useAchievements'
import { useSelectionMode } from '@/hooks/useSelectionMode'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { RATING_COLORS } from '@/lib/rating-colors'
import {
  SELF_RATING_OPTIONS, AI_RATING_OPTIONS, SORT_OPTIONS, DEFAULT_ORDERING,
  getToday, getWeekStart, getMonthStart, getYearStart,
} from '@/lib/achievementFilters'
import type { Achievement } from '@/types'

export default function AchievementTab() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [filterOpen, setFilterOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [selected, setSelected] = useState<Achievement | null>(null)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const selection = useSelectionMode()
  const isPC = useMediaQuery('(min-width: 768px)')
  const qc = useQueryClient()
  const [searchInput, setSearchInput] = useState(() => searchParams.get('search') ?? '')
  const dragStartY = useRef<number | null>(null)
  const [dragY, setDragY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isDragClosing, setIsDragClosing] = useState(false)

  // 検索ボタンで確定するまでの下書き状態（達成日・自己評価・AI評価）
  const [draftPreset, setDraftPreset] = useState<string | null>(() => searchParams.get('preset'))
  const [draftDateFrom, setDraftDateFrom] = useState(() => searchParams.get('date_from') ?? '')
  const [draftDateTo, setDraftDateTo] = useState(() => searchParams.get('date_to') ?? '')
  const [draftRating, setDraftRating] = useState<number[]>(() => searchParams.getAll('rating').map(Number).filter(Boolean))
  const [draftAiRating, setDraftAiRating] = useState<number[]>(() => searchParams.getAll('ai_rating').map(Number).filter(Boolean))

  const handleDragStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY
    setIsDragging(true)
  }
  const handleDragMove = (e: React.TouchEvent) => {
    if (dragStartY.current === null) return
    setDragY(Math.max(0, e.touches[0].clientY - dragStartY.current))
  }
  const handleDragEnd = () => {
    dragStartY.current = null
    if (dragY > 80) {
      setIsDragging(false)
      requestAnimationFrame(() => {
        setDragY(window.innerHeight)
        setTimeout(() => {
          setIsDragClosing(true)
          setFilterOpen(false)
        }, 250)
      })
    } else {
      setIsDragging(false)
      setDragY(0)
    }
  }

  const ordering = searchParams.get('ordering') ?? DEFAULT_ORDERING
  const dateFrom = searchParams.get('date_from') ?? ''
  const dateTo = searchParams.get('date_to') ?? ''
  const preset = searchParams.get('preset')
  const isAllPeriod = preset === '全期間'

  const today = getToday()
  const DATE_PRESETS = [
    { label: '全期間', from: '',               to: ''      },
    { label: '今週',   from: getWeekStart(),   to: today   },
    { label: '今月',   from: getMonthStart(),  to: today   },
    { label: '今年',   from: getYearStart(),   to: today   },
  ]

  const effectiveDateFrom = (isAllPeriod || !preset) ? undefined : (dateFrom || undefined)
  const effectiveDateTo   = (isAllPeriod || !preset) ? undefined : (dateTo   || undefined)

  // シートを開くたびに下書きを現在の確定値で初期化する
  const openFilter = () => {
    setDraftPreset(searchParams.get('preset'))
    setDraftDateFrom(searchParams.get('date_from') ?? '')
    setDraftDateTo(searchParams.get('date_to') ?? '')
    setDraftRating(searchParams.getAll('rating').map(Number).filter(Boolean))
    setDraftAiRating(searchParams.getAll('ai_rating').map(Number).filter(Boolean))
    setSearchInput(searchParams.get('search') ?? '')
    setDragY(0)
    setIsDragClosing(false)
    setFilterOpen(true)
  }

  const draftIsAllPeriod = draftPreset === '全期間'
  const draftPresetLabel = draftPreset ?? '全期間'
  const draftEffectiveFrom = (draftIsAllPeriod || !draftPreset) ? '' : draftDateFrom
  const draftEffectiveTo = (draftIsAllPeriod || !draftPreset) ? '' : draftDateTo

  const applyDraftPreset = (label: string, from: string, to: string) => {
    setDraftPreset(label)
    setDraftDateFrom(from)
    setDraftDateTo(to)
  }

  const setDraftDate = (key: 'from' | 'to', value: string) => {
    if (key === 'from') setDraftDateFrom(value)
    else setDraftDateTo(value)
    setDraftPreset(null)
  }

  const toggleDraftRating = (
    list: number[],
    setter: (next: number[]) => void,
    value: number,
  ) => {
    setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value])
  }

  // 検索ボタンで下書きを URL（＝確定値）へ反映する
  const applySearch = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      const search = searchInput.trim()
      if (search) next.set('search', search)
      else next.delete('search')

      next.delete('rating')
      draftRating.forEach((r) => next.append('rating', String(r)))
      next.delete('ai_rating')
      draftAiRating.forEach((r) => next.append('ai_rating', String(r)))

      if (draftDateFrom) next.set('date_from', draftDateFrom)
      else next.delete('date_from')
      if (draftDateTo) next.set('date_to', draftDateTo)
      else next.delete('date_to')
      if (draftPreset) next.set('preset', draftPreset)
      else next.delete('preset')

      return next
    })
    setFilterOpen(false)
  }

  const filters = {
    date_from: effectiveDateFrom,
    date_to: effectiveDateTo,
    rating: searchParams.getAll('rating').map(Number).filter(Boolean),
    ai_rating: searchParams.getAll('ai_rating').map(Number).filter(Boolean),
    ordering,
    search: searchParams.get('search') ?? undefined,
  }

  const setOrdering = (value: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (value === DEFAULT_ORDERING) {
        next.delete('ordering')
      } else {
        next.set('ordering', value)
      }
      return next
    })
  }

  const currentSortLabel = SORT_OPTIONS.find((o) => o.value === ordering)?.label ?? SORT_OPTIONS[0].label
  const isSorted = ordering !== DEFAULT_ORDERING

  const { data: achievements = [], isLoading } = useAchievements(filters)
  const deleteAchievement = useDeleteAchievement()
  const bulkResetAi = useBulkResetAiAnalysis()

  const clearAll = () => {
    setSearchInput('')
    setDraftPreset(null)
    setDraftDateFrom('')
    setDraftDateTo('')
    setDraftRating([])
    setDraftAiRating([])
    setSearchParams({})
  }

  const handleBulkDelete = async () => {
    await Promise.all(selection.selectedIds.map((id) => deleteAchievementApi(id)))
    qc.invalidateQueries({ queryKey: ['achievements'] })
    toast.success(`${selection.selectedIds.length}件を削除しました`)
    selection.clear()
  }

  const handleBulkResetAi = async () => {
    await bulkResetAi.mutateAsync(selection.selectedIds)
    toast.success(`${selection.selectedIds.length}件のAI判定をリセットしました`)
    selection.clear()
  }

  const handleDelete = (id: number) => {
    deleteAchievement.mutate(id, {
      onSuccess: () => {
        setSelected(null)
        toast.success('削除しました')
      },
      onError: () => toast.error('削除に失敗しました'),
    })
  }

  const hasFilters =
    filters.rating.length > 0 ||
    filters.ai_rating.length > 0 ||
    !!filters.search ||
    isAllPeriod ||
    !!dateFrom ||
    !!dateTo ||
    (!!preset && preset !== '今週')

  return (
    <div className="pb-4">
      <div className="sticky top-0 z-[3] bg-background -mx-4 px-4 flex justify-between items-center gap-2 py-2 border-b">
        {/* 凡例 */}
        <div className="flex flex-col gap-0.5 text-[10px] leading-tight text-muted-foreground">
          <span className="flex items-center gap-1">
            自己評価:
            <Star size={11} className={RATING_COLORS.self} />
          </span>
          <span className="flex items-center gap-1">
            AI評価:
            <Star size={11} className={RATING_COLORS.aiStar} />
            <span>＜</span>
            <Diamond size={11} className={RATING_COLORS.aiDiamond} />
          </span>
        </div>
        <div className="flex items-center gap-2">
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors min-h-[36px] ${
                isSorted
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-background text-muted-foreground border-border'
              }`}
            >
              <ArrowUpDown size={13} />
              {isSorted ? currentSortLabel : '並び替え'}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {SORT_OPTIONS.map((opt) => (
              <DropdownMenuCheckItem
                key={opt.value}
                checked={ordering === opt.value}
                onCheckedChange={() => setOrdering(opt.value)}
              >
                {opt.label}
              </DropdownMenuCheckItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <button
          type="button"
          onClick={openFilter}
          className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors min-h-[36px] ${
            hasFilters
              ? 'bg-primary text-primary-foreground border-primary'
              : 'bg-background text-muted-foreground border-border'
          }`}
        >
          <SlidersHorizontal size={13} />
          絞り込み
          {hasFilters && <span className="ml-0.5 h-1.5 w-1.5 rounded-full bg-primary-foreground" />}
        </button>
        {hasFilters && (
          <button
            type="button"
            onClick={clearAll}
            className="flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-destructive text-destructive bg-background min-h-[36px]"
          >
            <X size={12} />
            クリア
          </button>
        )}
        </div>
      </div>

      <Sheet open={filterOpen} onOpenChange={(open) => { if (open) setDragY(0); setFilterOpen(open) }}>
        <SheetContent
          side="bottom"
          className="rounded-t-2xl max-h-[80vh] overflow-y-auto"
          onOpenAutoFocus={(e) => e.preventDefault()}
          style={{
            transform: dragY > 0 ? `translateY(${dragY}px)` : undefined,
            transition: isDragging ? 'none' : 'transform 0.25s ease-out',
            animation: isDragClosing ? 'none' : undefined,
          }}
        >
          <div
            className="touch-none"
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            <div className="mx-auto mt-2 mb-1 h-1 w-10 rounded-full bg-muted-foreground/30" />
            <SheetHeader className="pb-2">
              <SheetTitle>絞り込み条件</SheetTitle>
            </SheetHeader>
          </div>
          <div className="px-4 pb-6 space-y-5">
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">キーワード</p>
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    applySearch()
                  }
                }}
                placeholder="実績名・タグ・AIタグ・メモで検索"
                autoComplete="off"
              />
            </div>

            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">達成日</p>
              <div className="flex flex-wrap gap-2">
                {DATE_PRESETS.map((p) => (
                  <button
                    key={p.label}
                    type="button"
                    onClick={() => applyDraftPreset(p.label, p.from, p.to)}
                    className={`text-xs px-3 py-1.5 rounded-full border transition-colors min-h-[36px] ${
                      draftPresetLabel === p.label
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-muted-foreground border-border'
                    }`}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={draftEffectiveFrom}
                  onChange={(e) => setDraftDate('from', e.target.value)}
                  className="text-xs"
                />
                <span className="text-xs text-muted-foreground shrink-0">〜</span>
                <Input
                  type="date"
                  value={draftEffectiveTo}
                  onChange={(e) => setDraftDate('to', e.target.value)}
                  className="text-xs"
                />
              </div>
            </div>

            <RatingFilterRow
              label="自己評価"
              options={SELF_RATING_OPTIONS}
              selected={draftRating}
              onToggle={(v) => toggleDraftRating(draftRating, setDraftRating, v)}
            />

            <RatingFilterRow
              label="AI評価"
              options={AI_RATING_OPTIONS}
              selected={draftAiRating}
              onToggle={(v) => toggleDraftRating(draftAiRating, setDraftAiRating, v)}
            />
          </div>
          <div className="sticky bottom-0 bg-background px-4 py-3 border-t">
            <Button className="w-full min-h-[44px]" onClick={applySearch}>
              検索
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {isPC && !selection.isSelecting && achievements.length > 0 && (
        <div className="flex justify-end mb-3">
          <Button variant="ghost" size="sm" onClick={selection.enter}>選択</Button>
        </div>
      )}

      {isLoading ? (
        <div className="py-8 text-center text-muted-foreground text-sm">読み込み中...</div>
      ) : achievements.length === 0 ? (
        <div className="py-16 text-center text-muted-foreground">
          <p className="text-sm">実績がありません</p>
          <p className="text-xs mt-1">右下の＋から登録してください</p>
        </div>
      ) : (
        <div className="space-y-3">
          {achievements.map((a) => (
            <AchievementCard
              key={a.id}
              achievement={a}
              onClick={selection.isSelecting ? undefined : () => setSelected(a)}
              isSelecting={selection.isSelecting}
              isSelected={selection.selectedIds.includes(a.id)}
              onSelect={selection.toggle}
              onLongPress={!isPC ? selection.enter : undefined}
            />
          ))}
        </div>
      )}

      {!selection.isSelecting && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              size="icon"
              className="fixed bottom-20 right-4 h-12 w-12 rounded-full shadow-lg"
            >
              <Plus size={24} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            side="top"
            className="mb-2"
            onCloseAutoFocus={(e) => e.preventDefault()}
          >
            <DropdownMenuItem className="gap-2 min-h-[44px]" onClick={() => setCreateOpen(true)}>
              <Pencil size={16} />
              手動登録
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 min-h-[44px]" onClick={() => setImportOpen(true)}>
              <Download size={16} />
              外部取り込み
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {selection.isSelecting && (
        <SelectionBar
          count={selection.selectedIds.length}
          onDelete={handleBulkDelete}
          onResetAi={handleBulkResetAi}
          onClear={selection.deselectAll}
          onCancel={selection.clear}
        />
      )}

      <AchievementCreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />

      <ExternalImportSheet open={importOpen} onClose={() => setImportOpen(false)} />

      <AchievementDetailDialog
        achievement={selected}
        onClose={() => setSelected(null)}
        onOpenDetail={() => {
          if (!selected) return
          const id = selected.id
          setSelected(null)
          navigate(`/achievements/${id}`)
        }}
        onDelete={() => setDeleteConfirmOpen(true)}
      />

      <ConfirmDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="実績を削除しますか？"
        description={selected ? `「${selected.title}」を削除します。この操作は元に戻せません。` : undefined}
        confirmLabel="削除する"
        variant="destructive"
        isPending={deleteAchievement.isPending}
        onConfirm={() => { if (selected) handleDelete(selected.id) }}
      />
    </div>
  )
}
